import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BookOpen, Paperclip, Scale, Search, Star, X } from 'lucide-react';
import { usePopoverDismissOutside } from './usePopoverDismissOutside';
import {
  buildParagraphLookupPrompt,
  mergeParagraphRefs,
  parseParagraphQuery,
  suggestParagraphRefs,
  type ParagraphRef,
} from './portalParagraphRef';
import {
  favoritesKeysToRefs,
  isParagraphFavorite,
  loadParagraphFavorites,
  saveParagraphFavorites,
  toggleParagraphFavorite,
} from './portalParagraphFavorites';

/**
 * Composer-Paragraphen-Suchleiste (ersetzt Analysieren-Shortcut-Duplikat).
 * Lesen = Lookup über rechtsfrage; Heften = §§ als Composer-Kontext.
 * §-Favoriten (Stern) = Haus-Paragraphen in localStorage — getrennt von Modus-/Tool-Pins.
 */

/** @deprecated Alias — früher Analysieren-Prefix; Lookup nutzt jetzt RECHTSFRAGE + buildParagraphLookupPrompt. */
export const ARKO_COMPOSER_SEARCH_PREFIX = 'Rechtsfrage (belegte Normen):\n\n';

const PILL =
  'inline-flex items-center gap-1 rounded-full border border-white/[0.08] bg-slate-900/70 px-2.5 py-1 text-[11px] font-medium text-slate-300 hover:border-violet-500/30 hover:bg-violet-500/5 hover:text-slate-100 transition-colors';

const PILL_ACTIVE =
  'inline-flex items-center gap-1 rounded-full border border-violet-500/40 bg-violet-500/10 px-2.5 py-1 text-[11px] font-medium text-white ring-1 ring-violet-400/25';

const ACTION_BTN =
  'inline-flex items-center gap-1 rounded-md border border-white/[0.1] bg-slate-900/80 px-2 py-1 text-[10px] font-medium text-slate-200 hover:border-violet-500/35 hover:bg-violet-500/10 disabled:opacity-40 disabled:cursor-not-allowed';

const RELATED_ACTION =
  'inline-flex items-center gap-0.5 rounded border border-white/[0.08] bg-slate-950/60 px-1.5 py-0.5 text-[9px] font-medium text-slate-300 hover:border-violet-500/35 hover:bg-violet-500/10 disabled:opacity-40 disabled:cursor-not-allowed';

export type ArkoComposerSearchChipProps = {
  attached: ParagraphRef[];
  onAttachedChange: (next: ParagraphRef[]) => void;
  /** Sofort nachschlagen (rechtsfrage / lookup-Pfad). */
  onLookup: (refs: ParagraphRef[], prompt: string) => void;
  /** Verwandte §§ aus lexProof.norm_chain (nach Lookup). UI-Block: related-norms. */
  relatedNorms?: ParagraphRef[];
  disabled?: boolean;
  className?: string;
  /**
   * Optional: User-ID für Favoriten-Prefs (localStorage-Key), analog Pins.
   * Ohne Session → Key `…:local`. Keine PII in den Prefs selbst.
   */
  favoritesUserId?: string | null;
};

export const ArkoComposerSearchChip: React.FC<ArkoComposerSearchChipProps> = ({
  attached,
  onAttachedChange,
  onLookup,
  relatedNorms = [],
  disabled = false,
  className = '',
  favoritesUserId = null,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const expectRelatedRef = useRef(false);
  const close = useCallback(() => setOpen(false), []);

  /* paragraph-favorites */
  const [favoriteKeys, setFavoriteKeys] = useState<string[]>(() =>
    loadParagraphFavorites(favoritesUserId),
  );

  useEffect(() => {
    setFavoriteKeys(loadParagraphFavorites(favoritesUserId));
  }, [favoritesUserId]);

  const persistFavorites = useCallback(
    (next: string[]) => {
      setFavoriteKeys(next);
      saveParagraphFavorites(next, favoritesUserId);
    },
    [favoritesUserId],
  );

  const toggleFavorite = useCallback(
    (ref: ParagraphRef) => {
      if (disabled || ref.section === '…') return;
      persistFavorites(toggleParagraphFavorite(favoriteKeys, ref.key));
    },
    [disabled, favoriteKeys, persistFavorites],
  );

  const favoriteRefs = useMemo(() => favoritesKeysToRefs(favoriteKeys), [favoriteKeys]);

  const parsed = useMemo(() => parseParagraphQuery(query), [query]);
  const suggestions = useMemo(() => suggestParagraphRefs(query, 6), [query]);
  const activeRefs = parsed.length > 0 ? parsed : suggestions.filter((s) => s.section !== '…');

  const excludeKeys = useMemo(() => {
    const keys = new Set(activeRefs.map((r) => r.key));
    for (const a of attached) keys.add(a.key);
    return keys;
  }, [activeRefs, attached]);

  const visibleRelated = useMemo(() => {
    if (!relatedNorms.length) return [];
    return relatedNorms.filter((r) => !excludeKeys.has(r.key)).slice(0, 8);
  }, [relatedNorms, excludeKeys]);

  /** Leere Suchleiste: Favoriten zuerst (Haus-§§), sonst Typeahead-Hints. */
  const showFavoriteList = open && !query.trim() && favoriteRefs.length > 0;
  const listRefs: ParagraphRef[] = showFavoriteList ? favoriteRefs : suggestions;

  usePopoverDismissOutside({ open, onClose: close, rootRef });

  useEffect(() => {
    if (open) {
      const t = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(t);
    }
  }, [open]);

  // Nach Lookup: Dropdown wieder öffnen, sobald verwandte §§ eintreffen.
  useEffect(() => {
    if (!expectRelatedRef.current) return;
    if (relatedNorms.length === 0) return;
    expectRelatedRef.current = false;
    setOpen(true);
  }, [relatedNorms]);

  const runLookup = useCallback(
    (refs: ParagraphRef[]) => {
      if (!refs.length || disabled) return;
      expectRelatedRef.current = true;
      onLookup(refs, buildParagraphLookupPrompt(refs));
      setQuery(refs.map((r) => r.label).join(', '));
      // Offen lassen / später per relatedNorms wieder öffnen — Related darunter.
    },
    [disabled, onLookup],
  );

  const runAttach = useCallback(
    (refs: ParagraphRef[]) => {
      if (!refs.length || disabled) return;
      onAttachedChange(mergeParagraphRefs(attached, refs));
      setQuery('');
    },
    [attached, disabled, onAttachedChange],
  );

  const detach = useCallback(
    (key: string) => {
      onAttachedChange(attached.filter((r) => r.key !== key));
    },
    [attached, onAttachedChange],
  );

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const refs = parsed.length > 0 ? parsed : activeRefs.slice(0, 1);
      if (refs.length) runLookup(refs);
    }
  };

  const previewLabel =
    parsed.length > 0
      ? parsed.map((r) => r.label).join(', ')
      : activeRefs[0]
        ? activeRefs[0].label
        : null;

  const showRelated =
    visibleRelated.length > 0 && (Boolean(previewLabel) || relatedNorms.length > 0);

  return (
    <div ref={rootRef} className={`relative inline-flex flex-col items-start gap-1 ${className}`}>
      <div className="inline-flex flex-wrap items-center gap-1">
        <button
          type="button"
          data-testid="portal-chip-paragraph"
          onClick={() => setOpen((v) => !v)}
          disabled={disabled}
          aria-expanded={open}
          aria-haspopup="dialog"
          title="Paragraph suchen — Nachschlagen oder an ARKO heften"
          className={`${open || attached.length > 0 ? PILL_ACTIVE : PILL} disabled:opacity-40 disabled:cursor-not-allowed`}
        >
          <Search className="w-3 h-3 shrink-0" aria-hidden />
          <span>Paragraph suchen</span>
          {attached.length > 0 ? (
            <span className="text-[10px] text-violet-300/90 tabular-nums">{attached.length}</span>
          ) : null}
        </button>

        {attached.map((ref) => (
          <span
            key={ref.key}
            className="inline-flex items-center gap-0.5 rounded-full border border-violet-500/30 bg-violet-500/10 pl-2 pr-1 py-0.5 text-[10px] font-medium text-violet-100"
            title="Angeheftet — geht als Kontext mit der nächsten Nachricht"
          >
            <Scale className="w-2.5 h-2.5 shrink-0 opacity-80" aria-hidden />
            {ref.label}
            <button
              type="button"
              onClick={() => detach(ref.key)}
              disabled={disabled}
              className="p-0.5 rounded-full text-violet-200/80 hover:text-white hover:bg-white/[0.08] disabled:opacity-40"
              aria-label={`${ref.label} lösen`}
            >
              <X className="w-2.5 h-2.5" aria-hidden />
            </button>
          </span>
        ))}
      </div>

      {open ? (
        <div
          role="dialog"
          aria-label="Paragraphen suchen"
          className="absolute bottom-full left-0 mb-1.5 z-40 w-[min(100vw-2rem,22rem)] rounded-xl border border-white/[0.1] bg-[#141619] shadow-[0_-8px_32px_rgba(0,0,0,0.45)] overflow-hidden"
        >
          <div className="p-2.5 space-y-2">
            <label className="block">
              <span className="sr-only">Paragraph suchen</span>
              <div className="flex items-center gap-1.5 rounded-lg border border-white/[0.1] bg-slate-950/80 px-2 py-1.5 focus-within:border-violet-500/40">
                <Search className="w-3.5 h-3.5 text-slate-500 shrink-0" aria-hidden />
                <input
                  ref={inputRef}
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={onKeyDown}
                  disabled={disabled}
                  placeholder="z. B. § 16 MRG oder § 24f LFG"
                  autoComplete="off"
                  className="w-full bg-transparent text-[12px] text-slate-100 placeholder:text-slate-600 focus:outline-none"
                />
              </div>
            </label>

            {previewLabel ? (
              <p className="text-[10px] text-slate-400 leading-snug px-0.5">
                Erkannt:{' '}
                <span className="text-slate-200 font-medium">{previewLabel}</span>
                {' — '}
                Vorschau über Nachschlagen im Normenbestand (kein Fall suchen).
              </p>
            ) : showFavoriteList ? (
              <p className="text-[10px] text-slate-500 leading-snug px-0.5">
                Favoriten — tippen zum Suchen, Stern zum Lösen; Lesen/Heften wie gewohnt.
              </p>
            ) : (
              <p className="text-[10px] text-slate-500 leading-snug px-0.5">
                Paragraph tippen, dann Lesen (Nachschlagen) oder an ARKO heften.
              </p>
            )}

            {listRefs.length > 0 ? (
              <ul className="max-h-36 overflow-y-auto custom-scrollbar space-y-0.5" role="listbox">
                {showFavoriteList ? (
                  <li className="px-2 pt-0.5 pb-1 text-[9px] uppercase tracking-wide text-slate-500">
                    Favoriten
                  </li>
                ) : null}
                {listRefs.map((s) => {
                  const fav = isParagraphFavorite(favoriteKeys, s.key);
                  const canFavorite = s.section !== '…';
                  return (
                    <li key={s.key} className="flex items-center gap-0.5">
                      <button
                        type="button"
                        role="option"
                        disabled={disabled || s.section === '…'}
                        onClick={() => {
                          if (s.section === '…') {
                            setQuery(`${s.law ?? ''} `);
                            return;
                          }
                          /* Favorit-Klick = bestehendes Lesen/Heften vorbereiten (Query setzen) */
                          setQuery(s.label);
                        }}
                        className="min-w-0 flex-1 text-left rounded-md px-2 py-1.5 text-[11px] text-slate-300 hover:bg-white/[0.05] disabled:opacity-50"
                      >
                        <span className="font-medium text-slate-100">{s.label}</span>
                        {s.section === '…' ? (
                          <span className="text-slate-500 ml-1">Gesetz wählen…</span>
                        ) : null}
                      </button>
                      {/* paragraph-favorites — Stern speichert Haus-§§; nicht Composer-Modus-Pin */}
                      {canFavorite ? (
                        <button
                          type="button"
                          disabled={disabled}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleFavorite(s);
                          }}
                          className={`shrink-0 p-1.5 rounded-md transition-colors disabled:opacity-40 ${
                            fav
                              ? 'text-amber-300 hover:text-amber-200 hover:bg-amber-500/10'
                              : 'text-slate-500 hover:text-slate-300 hover:bg-white/[0.05]'
                          }`}
                          title={fav ? 'Favorit lösen' : 'Als Favorit speichern'}
                          aria-label={fav ? `${s.label} aus Favoriten entfernen` : `${s.label} favorisieren`}
                          aria-pressed={fav}
                        >
                          <Star
                            className="w-3.5 h-3.5"
                            fill={fav ? 'currentColor' : 'none'}
                            aria-hidden
                          />
                        </button>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            ) : null}

            {/* paragraph-favorites — Stern auch am erkannten Treffer (Parsed), falls Liste nur Hints zeigt */}
            {parsed.length > 0 && !listRefs.some((r) => r.key === parsed[0].key) ? (
              <div className="flex items-center justify-between gap-2 px-0.5">
                <span className="text-[11px] text-slate-200 font-medium truncate">{parsed[0].label}</span>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => toggleFavorite(parsed[0])}
                  className={`shrink-0 p-1.5 rounded-md transition-colors disabled:opacity-40 ${
                    isParagraphFavorite(favoriteKeys, parsed[0].key)
                      ? 'text-amber-300 hover:text-amber-200 hover:bg-amber-500/10'
                      : 'text-slate-500 hover:text-slate-300 hover:bg-white/[0.05]'
                  }`}
                  title={
                    isParagraphFavorite(favoriteKeys, parsed[0].key)
                      ? 'Favorit lösen'
                      : 'Als Favorit speichern'
                  }
                  aria-label={
                    isParagraphFavorite(favoriteKeys, parsed[0].key)
                      ? `${parsed[0].label} aus Favoriten entfernen`
                      : `${parsed[0].label} favorisieren`
                  }
                  aria-pressed={isParagraphFavorite(favoriteKeys, parsed[0].key)}
                >
                  <Star
                    className="w-3.5 h-3.5"
                    fill={
                      isParagraphFavorite(favoriteKeys, parsed[0].key) ? 'currentColor' : 'none'
                    }
                    aria-hidden
                  />
                </button>
              </div>
            ) : null}

            <div className="flex flex-wrap gap-1.5 pt-0.5">
              <button
                type="button"
                className={ACTION_BTN}
                disabled={disabled || activeRefs.length === 0}
                onClick={() => runLookup(parsed.length > 0 ? parsed : activeRefs.slice(0, 1))}
                title="Nur nachschlagen — keine Fallanalyse"
              >
                <BookOpen className="w-3 h-3 shrink-0" aria-hidden />
                Lesen
              </button>
              <button
                type="button"
                className={ACTION_BTN}
                disabled={disabled || activeRefs.length === 0}
                onClick={() => runAttach(parsed.length > 0 ? parsed : activeRefs.slice(0, 1))}
                title="Paragraphen an ARKO heften — Kontext für die nächste Nachricht"
              >
                <Paperclip className="w-3 h-3 shrink-0" aria-hidden />
                An ARKO heften
              </button>
            </div>

            {/* related-norms — Favoriten/Stern hier nicht anfassen (Merge vs. Pins-Worker) */}
            {showRelated ? (
              <div className="border-t border-white/[0.06] pt-2 space-y-1.5" data-portal-related-norms>
                <p className="text-[10px] font-medium text-slate-400 px-0.5">
                  Verwandt / auch passend
                </p>
                <ul className="max-h-40 overflow-y-auto custom-scrollbar space-y-1" role="list">
                  {visibleRelated.map((r) => (
                    <li
                      key={r.key}
                      className="flex items-center gap-1.5 rounded-md px-2 py-1.5 bg-white/[0.02] hover:bg-white/[0.04]"
                    >
                      <Scale className="w-3 h-3 shrink-0 text-violet-400/80" aria-hidden />
                      <span className="flex-1 min-w-0 text-[11px] font-medium text-slate-100 truncate">
                        {r.label}
                      </span>
                      <button
                        type="button"
                        className={RELATED_ACTION}
                        disabled={disabled}
                        onClick={() => runLookup([r])}
                        title={`${r.label} nachschlagen`}
                      >
                        <BookOpen className="w-2.5 h-2.5 shrink-0" aria-hidden />
                        Lesen
                      </button>
                      <button
                        type="button"
                        className={RELATED_ACTION}
                        disabled={disabled}
                        onClick={() => runAttach([r])}
                        title={`${r.label} an ARKO heften`}
                      >
                        <Paperclip className="w-2.5 h-2.5 shrink-0" aria-hidden />
                        Heften
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};
